import { AlertTriangle, CheckCircle2, ChevronDown, Loader2, Mic, Pause, Play, Square, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  gravacoesConsultaControllerDescartar,
  gravacoesConsultaControllerEnviarTrecho,
  gravacoesConsultaControllerFinalizar,
  gravacoesConsultaControllerIniciar,
} from '@/api/generated/gravações-de-consulta/gravações-de-consulta';
import type { GravacaoConsultaResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { formatarDuracao, type Gravacao, useGravadorDeAudio } from '@/shared/hooks/useGravadorDeAudio';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import { toast } from '@/shared/ui/sonner';

/** Mesmo teto do backend (`DURACAO_MAXIMA_DA_CONSULTA_SEGUNDOS`). */
const DURACAO_MAXIMA_SEGUNDOS = 90 * 60;
/** Aviso antes do teto, para o veterinário encerrar com calma. */
const AVISO_DE_LIMITE_SEGUNDOS = 80 * 60;
/**
 * A cada 5 min gravados, o trecho fecha e sobe — invisível para o veterinário.
 * É o que limita a perda a 5 min se a aba fechar, e o que deixa o "finalizar"
 * rápido (quase tudo já chegou transcrito).
 */
const TRECHO_SEGUNDOS = 5 * 60;
/** Espera entre tentativas de reenvio de um trecho, com rede ruim. */
const ESPERAS_DE_REENVIO_MS = [2_000, 5_000, 10_000, 15_000];

type Etapa = 'consentimento' | 'gravando' | 'finalizando' | 'pronta';

interface Props {
  pacienteId: string;
  /** O rascunho de prontuário aberto: a gravação nasce vinculada a ele (e sai junto se ele for descartado). */
  prontuarioId?: string;
  /**
   * Gravação que o rascunho já tem (reabrindo com "Continuar"): o painel abre
   * direto no resultado. Se ela ficou pela metade (a aba fechou gravando), o
   * painel oferece processar o que foi salvo.
   */
  existente?: GravacaoConsultaResponse | null;
  /** A gravação terminou (transcrição + resumo, se a IA respondeu). O pai preenche o Conteúdo. */
  aoConcluir: (gravacao: GravacaoConsultaResponse) => void;
  /** Descartada — nada fica guardado. O pai esquece o `gravacaoConsultaId`. */
  aoDescartar: () => void;
  /** Gravando ou enviando: o pai não deixa fechar o diálogo sem confirmar. */
  aoMudarEmAndamento: (emAndamento: boolean) => void;
  /** O id no servidor, assim que existe (e `null` ao descartar) — para o pai descartar se o diálogo fechar. */
  aoMudarGravacaoId: (id: string | null) => void;
}

const esperar = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** 4xx (fora 408/429) não melhora tentando de novo: formato, tamanho, gravação já finalizada. */
function erroDefinitivo(erro: unknown): boolean {
  return erro instanceof ApiError && erro.status >= 400 && erro.status < 500 && ![408, 429].includes(erro.status);
}

/**
 * Consulta gravada (item 15), dentro do "Novo prontuário".
 *
 * Para o veterinário é Gravar → (Pausar) → Finalizar. Por baixo, o áudio sai
 * em trechos de 5 min enviados DURANTE a consulta, numa fila com reenvio: rede
 * de consultório cai, e um trecho que não subiu fica esperando em vez de se
 * perder. A tela só mostra "Gravação salva até mm:ss".
 */
export function GravacaoDeConsulta({
  pacienteId,
  prontuarioId,
  existente = null,
  aoConcluir,
  aoDescartar,
  aoMudarEmAndamento,
  aoMudarGravacaoId,
}: Props) {
  const [etapa, setEtapa] = useState<Etapa>(existente ? 'pronta' : 'consentimento');
  const [consentiu, setConsentiu] = useState(false);
  const [iniciando, setIniciando] = useState(false);
  const [resultado, setResultado] = useState<GravacaoConsultaResponse | null>(
    existente?.status === 'FINALIZADA' ? existente : null,
  );
  const [salvoAteSegundos, setSalvoAteSegundos] = useState(0);
  const [trechosNaFila, setTrechosNaFila] = useState(0);
  const [semConexao, setSemConexao] = useState(false);

  const gravacaoIdRef = useRef<string | null>(existente?.id ?? null);
  const filaRef = useRef<Gravacao[]>([]);
  const processandoFilaRef = useRef(false);
  const aoEsvaziarRef = useRef<Array<() => void>>([]);
  const falhouAoIniciarRef = useRef(false);
  const avisouLimiteRef = useRef(false);

  const gravador = useGravadorDeAudio({
    duracaoMaximaSegundos: DURACAO_MAXIMA_SEGUNDOS,
    trechoSegundos: TRECHO_SEGUNDOS,
    aoTrecho: (trecho) => enfileirar(trecho),
    aoAtingirLimite: (ultimo) => {
      toast.info('A gravação chegou ao limite de 90 minutos e foi encerrada.');
      void concluir(ultimo);
    },
    aoFalhar: (erro) => {
      falhouAoIniciarRef.current = true;
      if (erro === 'silencio') {
        toast.error('Não captamos som do microfone — a gravação foi descartada.', {
          description: 'Verifique se o microfone está ligado, sem mudo, e selecionado no navegador.',
          duration: 10_000,
        });
        void descartar();
      } else if (erro === 'permissao-negada') {
        toast.error('O microfone está bloqueado para este site.', {
          description:
            'Clique no cadeado ao lado do endereço, em "Microfone" escolha "Permitir" e recarregue a página.',
          duration: 12_000,
        });
      } else {
        toast.error(erro === 'sem-microfone' ? 'Nenhum microfone encontrado.' : 'Não foi possível gravar o áudio.');
      }
    },
  });

  const emAndamento = etapa === 'gravando' || etapa === 'finalizando';
  useEffect(() => aoMudarEmAndamento(emAndamento), [emAndamento, aoMudarEmAndamento]);

  // Fechar/recarregar a aba no meio da consulta: o navegador pede confirmação.
  useEffect(() => {
    if (!emAndamento) return;
    const segurar = (evento: BeforeUnloadEvent) => evento.preventDefault();
    window.addEventListener('beforeunload', segurar);
    return () => window.removeEventListener('beforeunload', segurar);
  }, [emAndamento]);

  useEffect(() => {
    if (gravador.segundos >= AVISO_DE_LIMITE_SEGUNDOS && !avisouLimiteRef.current) {
      avisouLimiteRef.current = true;
      toast.warning('Faltam 10 minutos para o limite de 90 minutos da gravação.');
    }
  }, [gravador.segundos]);

  // ── Fila de envio ──────────────────────────────────────────────────────────

  function enfileirar(trecho: Gravacao) {
    filaRef.current.push(trecho);
    setTrechosNaFila(filaRef.current.length);
    void processarFila();
  }

  /** Um trecho por vez, em ordem. O backend é idempotente por `ordem`: reenviar não duplica. */
  async function processarFila() {
    if (processandoFilaRef.current) return;
    processandoFilaRef.current = true;
    let tentativa = 0;

    while (filaRef.current.length > 0 && gravacaoIdRef.current) {
      const trecho = filaRef.current[0]!;
      try {
        const atual = await gravacoesConsultaControllerEnviarTrecho(gravacaoIdRef.current, {
          audio: trecho.audio,
          ordem: trecho.ordem,
          duracaoSegundos: trecho.duracaoSegundos,
        });
        setSalvoAteSegundos(atual.duracaoSegundos);
        filaRef.current.shift();
        tentativa = 0;
        setSemConexao(false);
      } catch (erro) {
        if (erroDefinitivo(erro)) {
          toast.error(erro instanceof ApiError ? erro.message : 'Um trecho da gravação foi recusado.');
          filaRef.current.shift();
        } else {
          setSemConexao(true);
          await esperar(ESPERAS_DE_REENVIO_MS[Math.min(tentativa, ESPERAS_DE_REENVIO_MS.length - 1)]!);
          tentativa += 1;
        }
      }
      setTrechosNaFila(filaRef.current.length);
    }

    processandoFilaRef.current = false;
    if (filaRef.current.length === 0) {
      aoEsvaziarRef.current.splice(0).forEach((resolver) => resolver());
    }
  }

  const filaVazia = () =>
    filaRef.current.length === 0 && !processandoFilaRef.current
      ? Promise.resolve()
      : new Promise<void>((resolver) => aoEsvaziarRef.current.push(resolver));

  // ── Ações ──────────────────────────────────────────────────────────────────

  async function comecar() {
    setIniciando(true);
    falhouAoIniciarRef.current = false;
    avisouLimiteRef.current = false;
    try {
      // Microfone primeiro: sem permissão, não se cria gravação nenhuma no servidor.
      await gravador.iniciar();
      if (falhouAoIniciarRef.current) return;

      try {
        const criada = await gravacoesConsultaControllerIniciar({
          pacienteId,
          ...(prontuarioId ? { prontuarioId } : {}),
          consentimentoTutor: true,
        });
        gravacaoIdRef.current = criada.id;
        aoMudarGravacaoId(criada.id);
      } catch (erro) {
        await gravador.cancelar();
        toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível iniciar a gravação.');
        return;
      }

      setSalvoAteSegundos(0);
      setEtapa('gravando');
      void processarFila(); // trecho que tenha fechado antes do id chegar
    } finally {
      setIniciando(false);
    }
  }

  async function concluir(ultimo: Gravacao | null) {
    setEtapa('finalizando');
    if (ultimo) enfileirar(ultimo);
    await filaVazia();

    const id = gravacaoIdRef.current;
    if (!id) return;
    await finalizarNoServidor(id);
  }

  async function finalizarNoServidor(id: string) {
    setEtapa('finalizando');
    try {
      const finalizada = await gravacoesConsultaControllerFinalizar(id);
      setResultado(finalizada);
      setEtapa('pronta');
      aoConcluir(finalizada);
    } catch (erro) {
      // A gravação está a salvo no servidor; só o processamento falhou.
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível processar a gravação agora.');
      setEtapa('pronta');
    }
  }

  async function finalizar() {
    const ultimo = await gravador.finalizar();
    // `null` sem gravação ativa = silêncio (o `aoFalhar` já descartou tudo).
    if (!ultimo && falhouAoIniciarRef.current) return;
    await concluir(ultimo);
  }

  async function descartar() {
    if (gravador.gravando) await gravador.cancelar();
    filaRef.current = [];
    setTrechosNaFila(0);
    const id = gravacaoIdRef.current;
    gravacaoIdRef.current = null;
    aoMudarGravacaoId(null);
    if (id) {
      try {
        await gravacoesConsultaControllerDescartar(id);
      } catch {
        // Já vinculada ou já apagada — nada a desfazer do lado da tela.
      }
    }
    setResultado(null);
    setConsentiu(false);
    setEtapa('consentimento');
    aoDescartar();
  }

  // ── Tela ───────────────────────────────────────────────────────────────────

  if (etapa === 'consentimento') {
    return (
      <div className="space-y-3 rounded-lg border border-brand/30 bg-brand/5 p-3" role="region" aria-label="Gravar consulta">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-brand">
          <Mic className="w-4 h-4" /> Gravar consulta
        </p>
        <p className="text-xs text-muted-foreground">
          A consulta é gravada e transcrita; ao finalizar, a IA monta um rascunho do prontuário para você revisar.
          O áudio fica anexado ao prontuário.
        </p>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={consentiu}
            onCheckedChange={(valor) => setConsentiu(valor === true)}
            aria-label="Consentimento do tutor"
            className="mt-0.5"
          />
          <span>Informei o tutor e ele concordou com a gravação da consulta.</span>
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={aoDescartar}>
            Cancelar
          </Button>
          <Button type="button" size="sm" disabled={!consentiu || iniciando} onClick={() => void comecar()}>
            {iniciando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
            Começar a gravar
          </Button>
        </div>
      </div>
    );
  }

  if (etapa === 'gravando') {
    return (
      <div
        role="group"
        aria-label="Gravando consulta"
        className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3"
      >
        <div className="flex items-center gap-3">
          <span
            className={`h-2.5 w-2.5 shrink-0 rounded-full ${gravador.pausado ? 'bg-muted-foreground' : 'animate-pulse bg-destructive'}`}
          />
          <span className="text-lg font-semibold tabular-nums text-foreground">{formatarDuracao(gravador.segundos)}</span>
          <div
            role="meter"
            aria-label="Volume do microfone"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(gravador.nivel * 100)}
            className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-muted"
          >
            <div
              className="h-full rounded-full bg-destructive transition-[width] duration-100"
              style={{ width: `${Math.max(4, gravador.nivel * 100)}%` }}
            />
          </div>
          <span className="flex-1 truncate text-xs text-muted-foreground">
            {gravador.pausado ? 'Pausada' : 'Gravando a consulta...'}
          </span>
        </div>

        {gravador.semSom && (
          <p role="status" className="text-xs font-medium text-destructive">
            Não estamos captando som do microfone.
          </p>
        )}

        <p className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="salvo-ate">
          {semConexao ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              Sem conexão — a gravação continua e será enviada assim que a rede voltar.
            </>
          ) : salvoAteSegundos > 0 ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              Gravação salva até {formatarDuracao(salvoAteSegundos)}
              {trechosNaFila > 0 && ' — enviando o restante...'}
            </>
          ) : (
            'A gravação é salva automaticamente enquanto você grava.'
          )}
        </p>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => void descartar()}>
            <Trash2 className="w-3.5 h-3.5 text-destructive" /> Descartar
          </Button>
          {gravador.pausado ? (
            <Button type="button" variant="outline" size="sm" onClick={gravador.retomar}>
              <Play className="w-3.5 h-3.5" /> Retomar
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={gravador.pausar}>
              <Pause className="w-3.5 h-3.5" /> Pausar
            </Button>
          )}
          <Button type="button" size="sm" onClick={() => void finalizar()}>
            <Square className="w-3.5 h-3.5" /> Finalizar
          </Button>
        </div>
      </div>
    );
  }

  if (etapa === 'finalizando') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-brand/30 bg-brand/5 p-3 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        {trechosNaFila > 0
          ? semConexao
            ? 'Sem conexão — aguardando a rede para enviar o final da gravação...'
            : 'Enviando o final da gravação...'
          : 'Transcrevendo e organizando a consulta...'}
      </div>
    );
  }

  // Pronta
  const naoTranscritos = resultado?.trechos.filter((trecho) => trecho.transcricaoStatus === 'FALHOU').length ?? 0;
  const semResumo = Boolean(resultado?.transcricao) && !resultado?.resumo;
  const nadaDito = resultado !== null && !resultado.transcricao;

  return (
    <div className="space-y-2 rounded-lg border border-brand/30 bg-brand/5 p-3" role="region" aria-label="Consulta gravada">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-brand">
        <Mic className="w-4 h-4" /> Consulta gravada
        {resultado && <span className="font-normal text-muted-foreground">· {formatarDuracao(resultado.duracaoSegundos)}</span>}
      </p>

      {!resultado ? (
        <p className="text-xs text-destructive">
          A gravação foi salva, mas ainda não foi processada. Tente de novo para gerar a transcrição.
        </p>
      ) : nadaDito ? (
        <p className="text-xs text-muted-foreground">Nenhuma fala foi identificada na gravação.</p>
      ) : semResumo ? (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Não foi possível gerar o resumo — a transcrição foi colocada no Conteúdo.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Rascunho gerado pela IA colocado no Conteúdo — <strong>revise antes de salvar</strong>.
        </p>
      )}

      {naoTranscritos > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {naoTranscritos === 1 ? '1 trecho não pôde ser transcrito.' : `${naoTranscritos} trechos não puderam ser transcritos.`}
        </p>
      )}

      {resultado?.transcricao && (
        <Collapsible>
          <CollapsibleTrigger className="group inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
            <ChevronDown className="w-3 h-3 transition-transform group-data-[state=open]:rotate-180" />
            Ver transcrição completa
          </CollapsibleTrigger>
          <CollapsibleContent>
            <p className="mt-1 max-h-60 overflow-y-auto rounded-md bg-background/60 px-2.5 py-1.5 text-xs text-muted-foreground whitespace-pre-wrap">
              {resultado.transcricao}
            </p>
          </CollapsibleContent>
        </Collapsible>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => void descartar()}>
          <Trash2 className="w-3.5 h-3.5 text-destructive" /> Descartar gravação
        </Button>
        {(semResumo || naoTranscritos > 0 || !resultado) && gravacaoIdRef.current && (
          <Button type="button" variant="outline" size="sm" onClick={() => void finalizarNoServidor(gravacaoIdRef.current!)}>
            Tentar de novo
          </Button>
        )}
      </div>
    </div>
  );
}
